import React, { Component } from "react";
import firebase from "firebase/app";
import "firebase/database";
import "firebase/auth";
import "firebase/storage";
import uuid from "uuid/v1";
import md5 from "md5";
import { firebaseConfig } from "../../config"; 

if (!firebase.apps.length) {
	firebase.initializeApp(firebaseConfig);
}

const db = firebase.database();
const auth = firebase.auth();
const storage = firebase.storage();

const defaultState = {
	loading: true,
	authUser: null,
	entries: {},
	competitions: {}
};

const FirebaseContext = React.createContext(defaultState);

class FirebaseProvider extends Component {
	state = defaultState;
	
	_refs = {};
	
	componentDidMount() {
		auth.onAuthStateChanged(async authUser => {
			if (authUser) {
				if (authUser.isAnonymous) {
					this.setState(() => ({ authUser, loading: false }));
				} else {
					// Check if this user has any roles
					const userRoles = await this.checkUserRoles(authUser.uid);
					if (userRoles === "admin") {
						authUser.isAdmin = true;
						this.setState(() => ({ authUser, loading: false }));
					}
				}
			} else {
				this.setState(() => ({ authUser: null, loading: false }));
			}
		});
	}
	
	subscribeToEntry = async id => {
		if (this._refs[id]) {
			return; //We're already subscribed
		}
		const ref = db.ref(`entries/${id}`);
		const onValueChange = ref.on("value", data => {
			const value = data.val();
			// Store the result in state which is accessible by all connected components
			this.setState({
				entries: {
					// Merge with the existing entries object
					...this.state.entries,
					[id]: value
				}
			});
		});
		
		this._refs[id] = { ref, onValueChange };
	};
	
	unsubscribeFromEntry = async id => {
		const { ref, onValueChange } = this._refs[id];
		await ref.off("value", onValueChange);
		delete this._refs[id];
	};
	
	subscribeToCompetition = async id => {
		if (this._refs[id]) {
			return; //We're already subscribed
		}
		const ref = db.ref(`competitions/${id}`);
		const onValueChange = ref.on("value", data => {
			const value = data.val();
			// Store the result in state which is accessible by all connected components
			this.setState({
				competitions: {
					// Merge with the existing entries object
					...this.state.competitions,
					[id]: value
				}
			});
		});
		
		this._refs[id] = { ref, onValueChange };
	};
	
	unsubscribeFromCompetition = async id => {
		const { ref, onValueChange } = this._refs[id];
		await ref.off("value", onValueChange);
		delete this._refs[id];
	};
	
	newEntry = async competitionId => {
		const id = uuid();
		await db.ref(`entries/${id}`).update({
			stage: 2,
			competition: competitionId,
			owner: this.state.authUser.uid
		});
		return id;
	};
	
	updateEntryData = async (id, data) => {
		// console.log("Updating", id, data);
		return await db.ref(`entries/${id}`).update({
			...data
		});
	};
	
	updateEntryStage = async (id, stage) => {
		await db.ref(`entries/${id}`).update({
			stage
		});
		// Refetch data
		return id;
	};
	
	addTeamMemberToEntry = async id => {
		const teamMemberId = uuid();
		// We can't set an empty object, so we have to give it a blank first name
		await db.ref(`entries/${id}/members/${teamMemberId}`).set({
			firstName: ""
		});
		return teamMemberId;
	};
	
	removeTeamMemberFromEntry = async (id, teamMemberId) => {
		await db.ref(`entries/${id}/members/${teamMemberId}`).set(null);
	};
	
	updateTeamMember = async (id, teamMemberId, data) => {
		await db.ref(`entries/${id}/members/${teamMemberId}`).update({
			...data
		});
	};
	
	updateTeamMembers = async (id, data) => {
		await db.ref(`entries/${id}/members`).update({
			...data
		});
	};
	
	uploadFile = async (filePath, file, progressCallback) => {
		const uploadTask = storage.ref(filePath).put(file);
		
		uploadTask.on("state_changed", snapshot => {
			var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
			if (progressCallback && typeof progressCallback === "function") {
				progressCallback(progress);
			}
		});
	};
	
	getFileUrl = async filePath => {
		const fileUrl = await storage.ref(filePath).getDownloadURL();
		return fileUrl;
	};
	
	addTalentToEntry = async id => {
		const talentId = uuid();
		// We can't set an empty object, so we have to give it a blank first name
		await db.ref(`entries/${id}/talent/${talentId}`).set({
			firstName: ""
		});
		return talentId;
	};
	
	removeTalentFromEntry = async (id, talentId) => {
		await db.ref(`entries/${id}/talent/${talentId}`).set(null);
	};
	
	updateTalent = async (id, talentId, data) => {
		await db.ref(`entries/${id}/talent/${talentId}`).update({
			...data
		});
	};
	
	signIn = (email, password) => {
		// Results from this one are handled by the onAuthStateChange
		return auth.signInWithEmailAndPassword(email, password);
	};
	
	signOut = () => {
		// Results from this one are handled by the onAuthStateChange
		return auth.signOut();
	};
	
	signInAnonymously = () => {
		return auth.signInAnonymously();
	};
	
	checkUserRoles = async uid => {
		const userRoles = await db.ref(`roles/${uid}`).once("value");
		return userRoles.val();
	};
	
	getCompetitions = async () => {
		const competitions = await db.ref(`competitions`).once("value");
		this.setState({
			competitions: competitions.val()
		});
	};
	
	getCompetition = async id => {
		const competition = await db.ref(`competitions/${id}`).once("value");
		this.setState({
			competitions: {
				...this.state.competitions,
				[id]: competition.val()
			}
		});
	};
	
	getCompetitionBySlug = async slug => {
		// Returns the competition ID for further data retrieval/storage
		const competition = await db
		.ref(`competitions`)
		.orderByChild("url")
		.equalTo(slug)
		.once("value");
		const [id, competitionObject] = Object.entries(competition.val())[0];
		await this.setState({
			competitions: {
				...this.state.competitions,
				[id]: competitionObject
			}
		});
		return id;
	};
	
	newCompetition = async data => {
		const id = uuid();
		await db.ref(`competitions/${id}`).update({
			...data
		});
		return id;
	};
	
	updateCompetition = async (id, data) => {
		await db.ref(`competitions/${id}`).update({
			...data
		});
		return id;
	};
	
	getCompetitionEntries = async id => {
		const entries = await db
		.ref(`entries`)
		.orderByChild("competition")
		.equalTo(id)
		.once("value");
		
		this.setState({
			entries: {
				...this.state.entries,
				...entries.val()
			}
		});
	};
	
	addAwardToCompetition = async id => {
		const awardId = uuid();
		// We can't set an empty object, so we have to give it a blank first name
		await db.ref(`competitions/${id}/awards/${awardId}`).set({
			name: ""
		});
		return awardId;
	};
	
	updateAward = async (id, awardId, data) => {
		await db.ref(`competitions/${id}/awards/${awardId}`).update({
			...data
		});
	};
	
	removeAward = async (id, awardId) => {
		await db.ref(`competitions/${id}/awards/${awardId}`).set(null);
	};
	
	addVideoToCompetition = async id => {
		const videoId = uuid();
		// We can't set an empty object, so we have to give it a blank first name
		await db.ref(`competitions/${id}/videos/${videoId}`).set({
			name: ""
		});
		return videoId;
	};
	
	updateVideo = async (id, videoId, data) => {
		await db.ref(`competitions/${id}/videos/${videoId}`).update({
			...data
		});
	};
	
	removeVideo = async (id, videoId) => {
		await db.ref(`competitions/${id}/videos/${videoId}`).set(null);
	};
	
	newVoter = async (id, data) => {
		const emailHash = md5(data.emailAddress);
		await db.ref(`voters/${id}/${emailHash}`).update({
			...data
		});
		return emailHash;
	};
	
	submitVotes = async (id, emailHash, votes) => {
		await db.ref(`votes/${id}/${emailHash}`).update({
			...votes
		});
	};
	
	checkIfVoted = async (id, emailHash) => {
		const vote = await db.ref(`votes/${id}/${emailHash}`).once("value");
		return vote.val();
	};
	
	checkIfVotedByEmail = async (id, email) => {
		const emailHash = md5(email);
		const vote = await db.ref(`votes/${id}/${emailHash}`).once("value");
		return vote.val();
	};
	
	render() {
		const { children } = this.props;
		const context = {
			...this.state,
			methods: {
				signIn: this.signIn,
				signOut: this.signOut,
				signInAnonymously: this.signInAnonymously,
				newEntry: this.newEntry,
				updateEntryData: this.updateEntryData,
				updateEntryStage: this.updateEntryStage,
				subscribeToEntry: this.subscribeToEntry,
				unsubscribeFromEntry: this.unsubscribeFromEntry,
				addTeamMemberToEntry: this.addTeamMemberToEntry,
				removeTeamMemberFromEntry: this.removeTeamMemberFromEntry,
				updateTeamMember: this.updateTeamMember,
				updateTeamMembers: this.updateTeamMembers,
				addTalentToEntry: this.addTalentToEntry,
				removeTalentFromEntry: this.removeTalentFromEntry,
				updateTalent: this.updateTalent,
				uploadFile: this.uploadFile,
				getCompetitions: this.getCompetitions,
				getCompetition: this.getCompetition,
				newCompetition: this.newCompetition,
				updateCompetition: this.updateCompetition,
				getCompetitionEntries: this.getCompetitionEntries,
				getCompetitionBySlug: this.getCompetitionBySlug,
				subscribeToCompetition: this.subscribeToCompetition,
				unsubscribeFromCompetition: this.unsubscribeFromCompetition,
				addAwardToCompetition: this.addAwardToCompetition,
				updateAward: this.updateAward,
				removeAward: this.removeAward,
				addVideoToCompetition: this.addVideoToCompetition,
				updateVideo: this.updateVideo,
				removeVideo: this.removeVideo,
				getFileUrl: this.getFileUrl,
				newVoter: this.newVoter,
				submitVotes: this.submitVotes,
				checkIfVoted: this.checkIfVoted,
				checkIfVotedByEmail: this.checkIfVotedByEmail
			}
		};
		return (
			<FirebaseContext.Provider value={context}>
			{children}
			</FirebaseContext.Provider>
		);
	}
}

const withFirebase = WrappedComponent => {
	return function FirebaseConnectedComponent(props) {
		return (
			<FirebaseContext.Consumer>
			{data => <WrappedComponent {...props} {...data} />}
			</FirebaseContext.Consumer>
		);
	};
};

export default FirebaseProvider;
export { withFirebase };



// WEBPACK FOOTER //
// ./src/components/Firebase/Firebase.js